/**
 * WorkSafePanel (13-28): the WorkSafe limb of the notification verb on an incident (FIELDSET §3.3.1:
 * `WorkSafeNotifiedAt`, `WorkSafeNotifiedByUPN`, `WorkSafeReference`, `WorkSafeWrittenRecordAt`).
 *
 * - **When:** `WorkSafeNotifiable` = Yes or `ReportabilityDecision` = `WorkSafe` (`isWorkSafeNotice`).
 * - **The due lines** (`lib/notificationDeadlines`, 13-27): the notice is due at `AwareAt`, "immediately"
 *   (OB-OHS-05, s 38(1)); the written record 48 hours after the duty to notify arose (OB-OHS-09, s 38(3)), anchored
 *   at the earlier of `AwareAt` and the notice (provisional, D-13-27-9). The notice-anchored time the plan names is
 *   shown beside it, for the ruling, and moves with the notice as it is typed. Amber once past while blank.
 * - **The written-record copy (D-13-L-6):** "Keep the copy for 5 years (OB-OHS-11)", attached on the SharePoint item
 *   as `INC-<Id>-worksafe-record`, with the link to the item's form. No upload control.
 * - **The WorkSafe exchanges:** "Log a WorkSafe exchange" (`Notification`, §3.4.1 G-194) and "WorkSafe asked for
 *   information" (`InformationRequest`, G-178), through `SiblingComposer` under the notification verb.
 * - **Who** (D-13-L-2): HR & WHS (the gate's WorkSafe notice). Anyone else with a register role reads it.
 * The notifier starts as the signed-in person (editable). Saves are synchronous, confirmed and eTag-guarded (D-13-L-4).
 */

import { useMemo, useState } from 'react';
import VerbForm from '../VerbForm';
import SiblingComposer from '../SiblingComposer';
import { Button } from '../../ui';
import { fieldOf } from '../../../config/fieldset/registerFields';
import { formatInstant, melbourneWallToInstant } from '../../../lib/fieldValues';
import { worksafeWrittenRecordDue } from '../../../lib/notificationDeadlines';
import type { DeadlineRule } from '../../../lib/notificationDeadlines';
import { NOTICE_COLUMNS, WORKSAFE_DELEGATES_LINE, canDoRecordVerb } from '../../../lib/recordAuthority';
import { valueOf } from '../../../lib/recordView';
import { WRITER_OF_VERB } from '../../../lib/recordVerbs';
import { isPracticeRef } from '../../../lib/recordQuery';
import { noticeRules } from '../../../lib/regulatoryPanels';
import { composerKinds } from '../../../lib/siblingComposer';
import { NOT_RECORDED, timeKeyOf } from '../../../lib/verbForm';
import type { FormValues } from '../../../lib/verbForm';
import { worksafeRecordName } from '../../../lib/whsPanels';
import { recordEditFormUrl } from '../../../services/lodgeService';
import type { RecordRow } from '../../../types/records';
import type { PanelProps } from './registry';
import record from '../record.module.css';
import styles from './WhsPanel.module.css';

const I = 'incidents';
const NOTICE = 'WorkSafeNotifiedAt';
const WRITTEN = 'WorkSafeWrittenRecordAt';
const COLUMNS = NOTICE_COLUMNS.worksafe;
if (!COLUMNS.includes(NOTICE) || !COLUMNS.includes(WRITTEN) || COLUMNS.length !== 4) {
  throw new Error('WorkSafePanel: the WorkSafe notice columns are not the four FIELDSET names');
}

/** The notification verb's WorkSafe rows (§3.4.1 G-194, G-178), with the panel's words. */
const ROW_BUTTONS: Readonly<Record<string, string>> = {
  Notification: 'Log a WorkSafe exchange',
  InformationRequest: 'WorkSafe asked for information',
};
const ROW_KINDS = composerKinds(I, WRITER_OF_VERB.notification).filter((k) => k.logKind && ROW_BUTTONS[k.logKind]);
if (ROW_KINDS.length !== 2) throw new Error('WorkSafePanel: the WorkSafe exchange rows are not in the §3.4.1 map');

/** D-13-L-2 / D-13-33: HR & WHS, then the named delegates in order, by role. */
const WHO = ['HR & WHS records the WorkSafe notice.', WORKSAFE_DELEGATES_LINE].filter(Boolean).join(' ');

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** A typed instant from the form, else the record's. */
function instantOf(row: RecordRow, values: FormValues | null, column: string): string | null {
  if (values) {
    const day = values[column] ?? '';
    const time = values[timeKeyOf(column)] ?? '';
    if (day && time) {
      const r = melbourneWallToInstant(day, time);
      return r.ok ? r.value : null;
    }
    if (column in values) return null;
  }
  return text(row.fields[column]) || null;
}

function Cite({ rule }: { rule: DeadlineRule }) {
  return (
    <details className={styles.why}>
      <summary>Why</summary>
      <ul>
        <li>
          <span className={styles.cite}>{rule.obRow}</span> · {rule.section}: &ldquo;{rule.period}&rdquo;. Read as {rule.reading}.
        </li>
        {rule.provisional && <li>Provisional: {rule.provisional}.</li>}
      </ul>
    </details>
  );
}

/** Due against done for one WorkSafe instant (amber once past while blank, D-13-15-4). */
function DueLine({ rule, given, now }: { rule: DeadlineRule; given: string | null; now: Date }) {
  const when = formatInstant(rule.dueAt);
  if (given) {
    const late = Date.parse(given) > Date.parse(rule.dueAt);
    return (
      <>
        <p className={record.hint}>
          Given {formatInstant(given)}; it was due {when}
          {late ? ', so it was given after the due time' : ''}.
        </p>
        <Cite rule={rule} />
      </>
    );
  }
  const overdue = Date.parse(rule.dueAt) < now.getTime();
  return (
    <>
      {overdue ? (
        <p className={record.notice} role="status">
          <strong>Overdue.</strong> It was due {when}: {rule.reading}.
        </p>
      ) : (
        <p className={record.hint}>
          <strong>Due {when}</strong>: {rule.reading}.
        </p>
      )}
      <Cite rule={rule} />
    </>
  );
}

/** The notice's due line: `AwareAt` itself (OB-OHS-05). */
function NoticeDue({ row, values, now }: { row: RecordRow; values: FormValues | null; now: Date }) {
  const rule = noticeRules(row).find((r) => r.key === 'worksafe');
  if (!rule) {
    return (
      <p className={record.hint}>
        When Harmony became aware isn&rsquo;t recorded yet, so the clock can&rsquo;t be shown. It is recorded in the <a href="#panel-whs">WHS panel</a>.
      </p>
    );
  }
  return <DueLine rule={rule} given={instantOf(row, values, NOTICE)} now={now} />;
}

/** The written record's due line, the notice-anchored reading beside it, and where the copy is kept. */
function WrittenDue({ row, values, now }: { row: RecordRow; values: FormValues | null; now: Date }) {
  const notice = instantOf(row, values, NOTICE);
  const aware = text(row.fields.AwareAt) || null;
  const rule = worksafeWrittenRecordDue(notice, aware);
  const fromNotice = notice ? worksafeWrittenRecordDue(notice, null) : null;
  const given = instantOf(row, values, WRITTEN);
  const editUrl = isPracticeRef(row.ref) ? null : recordEditFormUrl(row.register, row.ref);
  const name = worksafeRecordName(row.ref);
  return (
    <>
      {rule ? <DueLine rule={rule} given={given} now={now} /> : <p className={record.hint}>The 48 hours start once the aware time or the notice is recorded.</p>}
      {rule && fromNotice && fromNotice.dueAt !== rule.dueAt && (
        <p className={record.hint}>
          If the ruling anchors the 48 hours at the notice instead: {formatInstant(fromNotice.dueAt)} (D-13-27-9).
        </p>
      )}
      <p className={record.hint}>
        Keep the copy for 5 years (OB-OHS-11 · OHSA-VIC-2004-NOTIF s 38(4)): attach it on the SharePoint item as <code>{name}</code>.{' '}
        {editUrl ? (
          <a href={editUrl} target="_blank" rel="noreferrer">
            Open the item&rsquo;s form
          </a>
        ) : (
          'A practice record has no SharePoint item, so there is no link.'
        )}
      </p>
    </>
  );
}

function ReadOnlyView({ row }: { row: RecordRow }) {
  const [now] = useState(() => new Date());
  return (
    <div className={record.verbForm}>
      <p className={styles.who}>
        <strong>{WHO}</strong> You can read it here.
      </p>
      <dl className={record.facts}>
        {COLUMNS.map((column) => {
          const spec = fieldOf(I, column)!;
          const v = valueOf(spec, row);
          return (
            <div key={column} style={{ display: 'contents' }}>
              <dt>{spec.label}</dt>
              <dd>{v.value === null ? <span className={record.blank}>{NOT_RECORDED}</span> : v.value}</dd>
            </div>
          );
        })}
      </dl>
      <p className={record.label}>{fieldOf(I, NOTICE)?.label}</p>
      <NoticeDue row={row} values={null} now={now} />
      <p className={record.label}>{fieldOf(I, WRITTEN)?.label}</p>
      <WrittenDue row={row} values={null} now={now} />
    </div>
  );
}

export default function WorkSafePanel({ row, siblings, actor, readOnly, onSaved }: PanelProps) {
  const may = !readOnly && canDoRecordVerb('notification', row, actor, { notice: 'worksafe' }).ok;
  const [now] = useState(() => new Date());
  const [rowKind, setRowKind] = useState<string | null>(null);
  const fixedKinds = useMemo(() => Object.fromEntries(ROW_KINDS.map((k) => [k.logKind as string, [k]])), []);
  const defaults = useMemo(
    (): Record<string, string> => (text(row.fields.WorkSafeNotifiedByUPN) === '' ? { WorkSafeNotifiedByUPN: actor.upn.toLowerCase() } : {}),
    [row, actor.upn],
  );

  if (!may) return <ReadOnlyView row={row} />;

  const fieldNote = (column: string, values: FormValues) => {
    if (column === NOTICE) return <NoticeDue row={row} values={values} now={now} />;
    if (column === WRITTEN) return <WrittenDue row={row} values={values} now={now} />;
    if (column === 'WorkSafeNotifiedByUPN') return <p className={record.hint}>Who notified WorkSafe (OB-OHS-07). It starts as you.</p>;
    if (column === 'WorkSafeReference') return <p className={record.hint}>WorkSafe&rsquo;s reference for the notice, when they give one (OB-OHS-08). It can be added later.</p>;
    return null;
  };

  return (
    <div className={record.verbForm}>
      <VerbForm
        register={I}
        verb="notification"
        row={row}
        siblings={siblings}
        actor={actor}
        columns={COLUMNS}
        extraInputs={{ ctx: { notice: 'worksafe' } }}
        defaults={defaults}
        fieldNote={fieldNote}
        saveLabel="Save the WorkSafe notice"
        onSaved={onSaved}
        lead={
          <p className={styles.who}>
            <strong>You record the WorkSafe notice, as HR &amp; WHS.</strong> Record the notice and the written record as each is given; a blank
            means it hasn&rsquo;t been given yet.
          </p>
        }
      />
      <div className={styles.rowsBlock}>
        <p className={record.label}>WorkSafe&rsquo;s side</p>
        <p className={record.hint}>Each is its own row on the trail, under your name. Write what happened; contact details go in their own fields.</p>
        <div className={styles.rowButtons}>
          {ROW_KINDS.map((k) => (
            <Button
              key={k.key}
              type="button"
              variant="secondary"
              size="sm"
              className={rowKind === k.logKind ? styles.rowPicked : undefined}
              aria-pressed={rowKind === k.logKind}
              onClick={() => setRowKind(rowKind === k.logKind ? null : (k.logKind as string))}
            >
              {ROW_BUTTONS[k.logKind as string]}
            </Button>
          ))}
        </div>
        {rowKind && (
          <SiblingComposer
            key={rowKind}
            row={row}
            actor={actor}
            verb="notification"
            kinds={fixedKinds[rowKind]}
            gate={{ notice: 'worksafe' }}
            title={ROW_BUTTONS[rowKind]}
            intro="WorkSafe Victoria. One row on the trail, under your name, dated when it happened."
            onSaved={(saved) => {
              setRowKind(null);
              onSaved(saved);
            }}
          />
        )}
      </div>
    </div>
  );
}
